import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Download, File, Folder, RefreshCw, Trash2 } from "lucide-react";
import { match } from "ts-pattern";
import { Button } from "@/components/agent/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/atomic/DropdownMenu";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { useSandboxFile } from "@/hooks/agent/useSandboxFile";
import { useSandboxFiles } from "@/hooks/agent/useSandboxFiles";
import { getAgentThreadStore } from "@/stores/agent/agentThreadStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./AgentDialog";

interface FileListContentProps {
  folderPaths: string[];
  filesByFolder: Record<string, string[]>;
  isFile: (filePath: string) => boolean;
  getFileName: (filePath: string) => string;
  handleDownload: (filePath: string) => Promise<void>;
  handleDelete: (filePath: string) => Promise<void>;
  isDownloadingFile: (filePath: string) => boolean;
  deletingFile: string | null;
}

const FileListContent: React.FC<FileListContentProps> = ({
  folderPaths,
  filesByFolder,
  isFile,
  getFileName,
  handleDownload,
  handleDelete,
  isDownloadingFile,
  deletingFile,
}) => {
  return (
    <div className="flex flex-col gap-6">
      {folderPaths.map((folderPath) => (
        <div key={folderPath} className="flex flex-col gap-2">
          {/* フォルダパス */}
          <div className="text-sm font-medium text-gray-500">
            {folderPath}
          </div>

          {/* ファイル一覧 */}
          <div className="flex flex-col gap-1">
            {filesByFolder[folderPath].map((filePath) => (
              <div
                key={filePath}
                className="flex items-center gap-3 rounded-lg pr-3 py-2 hover:bg-gray-50"
              >
                {/* ファイル/フォルダアイコン */}
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                    isFile(filePath) ? "bg-gray-100" : "bg-blue-100"
                  }`}
                >
                  {isFile(filePath)
                    ? <File className="h-5 w-5 text-gray-600" />
                    : <Folder className="h-5 w-5 text-blue-600" />}
                </div>

                {/* ファイル情報 */}
                <div className="flex flex-1 flex-col">
                  <div className="text-sm font-medium">
                    {getFileName(filePath)}
                  </div>
                </div>

                {/* アクションメニュー */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-transparent cursor-pointer text-gray-500"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(isDownloadingFile(filePath) || deletingFile === filePath)
                        ? (
                          <>
                            <span className="inline-flex text-base leading-none ">
                              <span
                                style={{
                                  animation: "dotPulse 1.8s ease-in-out infinite",
                                  animationDelay: "0s",
                                }}
                              >
                                •
                              </span>
                              <span
                                style={{
                                  animation: "dotPulse 1.8s ease-in-out infinite",
                                  animationDelay: "0.15s",
                                }}
                              >
                                •
                              </span>
                              <span
                                style={{
                                  animation: "dotPulse 1.8s ease-in-out infinite",
                                  animationDelay: "0.3s",
                                }}
                              >
                                •
                              </span>
                            </span>
                            <style>
                              {`
                            @keyframes dotPulse {
                              0%, 100% { opacity: 0.2; }
                              50% { opacity: 1; }
                            }
                          `}
                            </style>
                          </>
                        )
                        : <span className="inline-flex text-base leading-none">•••</span>}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isFile(filePath) && (
                      <>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(filePath);
                          }}
                          disabled={isDownloadingFile(filePath)}
                          className="cursor-pointer"
                        >
                          <Download className="h-4 w-4" />
                          ダウンロード
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(filePath);
                      }}
                      disabled={deletingFile !== null}
                      className="text-red-600 cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />
                      削除
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

interface FileBrowserModalProps {
  threadId: string;
}

export const FileBrowserModal: React.FC<FileBrowserModalProps> = ({
  threadId,
}) => {
  const open = getAgentThreadStore(threadId)((state) => state.isOpenFileBrowser);
  const setIsOpenFileBrowser = getAgentThreadStore(threadId)((state) => state.setIsOpenFileBrowser);
  const threadPath = threadId ? `/workspace/${threadId}` : null;
  const {
    files,
    filesByFolder,
    folderPaths,
    isLoading,
    isRefreshing,
    hasLoaded,
    mutate,
    getFileName,
    isFile,
  } = useSandboxFiles(open ? threadPath : null);
  const { downloadFile, deleteFile, isDownloadingFile, deletingFile } = useSandboxFile();

  // ファイルダウンロード
  const handleDownload = async (filePath: string) => {
    await downloadFile(filePath);
  };

  // ファイル削除
  const handleDelete = async (filePath: string) => {
    await deleteFile(filePath);
    // ファイル一覧を再取得
    await mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => setIsOpenFileBrowser?.(isOpen)}>
      <DialogContent className="max-w-[800px]">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>ファイル一覧</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        <div className="flex flex-col gap-4">
          {/* モーダルタイトル */}
          <div className="flex items-center justify-between flex-shrink-0 text-xl font-semibold pt-6 px-6">
            <span>ファイル一覧</span>
          </div>

          {/* ファイル数とリロードボタン */}
          {hasLoaded && (
            <div className="text-sm text-gray-500 flex-shrink-0 px-6 flex items-center justify-between">
              <span>{files.filter(f => isFile(f)).length} files</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => mutate()}
                disabled={isRefreshing}
                className="h-6 w-6 p-0"
              >
                <RefreshCw className={`h-3 w-3 ${isRefreshing ? "animate-spin" : ""}`} />
              </Button>
            </div>
          )}

          {/* ファイル一覧 */}
          <ScrollArea className="flex-1 min-h-100 max-h-[calc(100vh-360px)] pl-6 pr-2 pb-2">
            {match({ isLoading: isLoading && files.length === 0, hasFiles: folderPaths.length > 0 })
              .with({ isLoading: true }, () => (
                <div className="py-36 text-center text-sm text-gray-500">
                  Loading...
                </div>
              ))
              .with(
                { isLoading: false, hasFiles: false },
                () => (
                  <div className="py-36 text-center text-sm text-gray-500">
                    ファイルがありません
                  </div>
                ),
              )
              .otherwise(() => (
                <FileListContent
                  folderPaths={folderPaths}
                  filesByFolder={filesByFolder}
                  isFile={isFile}
                  getFileName={getFileName}
                  handleDownload={handleDownload}
                  handleDelete={handleDelete}
                  isDownloadingFile={isDownloadingFile}
                  deletingFile={deletingFile}
                />
              ))}
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
